import React, { useEffect, useMemo, useState } from "react";
import {
  getCurrentEmployeePolicyAssignment,
  getEmployeeMasters,
  getEmployeePolicyHistory,
  getHRSettingsMasterData,
  saveEmployeeMaster,
} from "../../services/employeeService";
import { getPayScaleBands } from "../../services/ctcService";
import EmployeeMasterTable from "./EmployeeMasterTable";
import EmployeePolicyHistoryPanel from "./EmployeePolicyHistoryPanel";

import "../../styles/hrPageShell.css";
const FALLBACK_COUNTRY_OPTIONS = ["India", "Germany", "Global / Other"];
const FALLBACK_INDUSTRY_TYPES = [
  "Office / IT / Services",
  "Manufacturing",
  "Processing",
  "Plant Operations",
  "Warehouse / Logistics",
  "Field Service",
  "Retail",
  "Other",
];
const FALLBACK_EMPLOYMENT_TYPES = ["Full Time", "Part Time", "Contract", "Trainee", "Intern", "Consultant", "Shift Worker"];
const FALLBACK_TIME_ZONES = ["Asia/Kolkata", "Europe/Berlin", "UTC"];
const WORK_ARRANGEMENT_OPTIONS = ["Office", "Remote", "Hybrid", "Plant / Shopfloor", "Field", "Hourly Shift"];
const FTE_PERCENT_OPTIONS = ["100", "75", "50", "25", "0"];

const emptyMasterData = {
  countries: [],
  legalEntities: [],
  locations: [],
  departments: [],
  workUnits: [],
  employmentTypes: [],
  attendancePolicies: [],
  leavePolicies: [],
  leavePolicyRules: [],
  shiftPatterns: [],
  weeklyOffPatterns: [],
  holidayCalendars: [],
  holidayCalendarDates: [],
  roleScopeAssignments: [],
  employeePolicyAssignments: [],
};

const initialFormData = {
  id: "",
  ID: "",
  employeeId: "",
  workEmail: "",
  legalEntity: "",
  legalEntityCode: "",
  country: "India",
  countryCode: "IN",
  regionState: "",
  location: "",
  locationCode: "",
  workLocationId: "",
  industryType: "Office / IT / Services",
  department: "",
  departmentCode: "",
  productionUnitCode: "",
  workUnitCode: "",
  designation: "",
  gradeLevel: "",
  reportingManager: "",
  managerEmployeeCode: "",
  joiningDate: "",
  employmentStatus: "Offered",
  employmentType: "Full Time",
  workArrangement: "Office",
  ftePercent: "100",
  timeZone: "Asia/Kolkata",
  leavePolicyId: "",
  attendancePolicyId: "",
  shiftPatternId: "",
  shiftPolicyId: "",
  weeklyOffPatternId: "",
  holidayCalendarId: "",
  payGrade: "",
  salaryCurrency: "INR",
  compensationStatus: "Pending Entry",
  currentEmployeeCTCId: "",
  currentCTCStructureId: "",
  currentCTCEffectiveFrom: "",
  currentCTCStatus: "",
  annualCTC: "",
  monthlyCTC: "",
  monthlyGross: "",
  annualEmployerCost: "",
  netPayEstimateMonthly: "",
  cTCLastApprovedOn: "",
  cTCRevisionNumber: "",
  revisionDueFlag: false,
  profileCompletionStatus: "Not Started",
  profileVerificationStatus: "Not Verified",
  onboardingStage: "Offered",
};

function normalizeText(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value).trim();
  if (Array.isArray(value)) return value.map(extractSharePointValue).filter(Boolean).join(";");
  if (typeof value === "object") return normalizeText(value.Value || value.value || value.Title || value.Email || value.DisplayName || "");
  return String(value).trim();
}

function normalizeKey(key) {
  return normalizeText(key).toLowerCase().replace(/[^a-z0-9]/g, "");
}

function extractSharePointValue(value) {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return value.map(extractSharePointValue).filter(Boolean).join(";");
  if (typeof value === "object") {
    return normalizeText(value.Value || value.value || value.Title || value.Email || value.DisplayName || "");
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if ((trimmed.startsWith("[") && trimmed.endsWith("]")) || (trimmed.startsWith("{") && trimmed.endsWith("}"))) {
      try {
        const parsed = JSON.parse(trimmed);
        return extractSharePointValue(parsed);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  return normalizeText(value);
}

function getField(record, ...candidateNames) {
  if (!record || typeof record !== "object") return "";
  for (const name of candidateNames) {
    if (Object.prototype.hasOwnProperty.call(record, name)) return record[name];
  }
  const normalizedLookup = Object.keys(record).reduce((acc, key) => {
    acc[normalizeKey(key)] = record[key];
    return acc;
  }, {});
  for (const name of candidateNames) {
    const normalizedName = normalizeKey(name);
    if (Object.prototype.hasOwnProperty.call(normalizedLookup, normalizedName)) {
      return normalizedLookup[normalizedName];
    }
  }
  return "";
}

function getArray(rawData, ...candidateNames) {
  const direct = getField(rawData, ...candidateNames);
  if (Array.isArray(direct)) return direct;
  if (typeof direct === "string") {
    try {
      const parsed = JSON.parse(direct);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function splitMultiValue(value) {
  const extracted = extractSharePointValue(value);
  return extracted
    .split(/[;,]/)
    .map((item) => extractSharePointValue(item).trim())
    .filter(Boolean)
    .filter((item) => !item.startsWith("@odata.type") && item !== "0");
}

function getCountryNameFromCode(countryCode) {
  const code = normalizeText(countryCode).toUpperCase();
  if (code === "IN") return "India";
  if (code === "DE") return "Germany";
  if (code === "GLB" || code === "ALL") return "Global / Other";
  return normalizeText(countryCode);
}

function getCountryCodeFromName(countryName) {
  const name = normalizeText(countryName);
  if (name === "India") return "IN";
  if (name === "Germany") return "DE";
  if (name === "Global / Other") return "GLB";
  return name;
}

function isActiveRecord(record) {
  const value = getField(record, "IsActive", "Is Active", "Active");
  return value === true || value === "Yes" || value === "yes" || value === 1 || value === "1" || value === undefined || value === null || value === "";
}

function mapOption(value, label, extra = {}) {
  const normalizedValue = normalizeText(value);
  const normalizedLabel = normalizeText(label || value);
  if (!normalizedValue && !normalizedLabel) return null;
  return {
    value: normalizedValue || normalizedLabel,
    label: normalizedLabel || normalizedValue,
    ...extra,
  };
}

function compactOptions(options) {
  const seen = new Set();
  return options.filter(Boolean).filter((option) => {
    if (!option.value) return false;
    if (seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
}
function addCleanOption(values, rawValue) {
  splitMultiValue(rawValue).forEach((item) => {
    const clean = extractSharePointValue(item);
    if (clean && !clean.includes("@odata.type") && !clean.includes("Microsoft.Azure.Connectors") && clean !== "0") {
      values.add(clean);
    }
  });
}

function normalizeMasterData(rawData = {}) {
  const source = rawData?.body || rawData || {};

  const countries = compactOptions(getArray(source, "countries", "Countries")
    .filter(isActiveRecord)
    .map((item) => {
      const countryCode = getField(item, "CountryCode", "Country Code");
      const countryName = getField(item, "CountryName", "Country Name", "Title") || getCountryNameFromCode(countryCode);
      return mapOption(countryName, countryName, {
        countryCode: countryCode || getCountryCodeFromName(countryName),
        defaultCurrency: getField(item, "DefaultCurrency", "Default Currency"),
        defaultTimeZone: getField(item, "DefaultTimeZone", "Default TimeZone", "Default Time Zone"),
        displayOrder: Number(getField(item, "DisplayOrder", "Display Order") || 999),
      });
    }))
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const legalEntities = compactOptions(getArray(source, "legalEntities", "LegalEntities", "legal entities")
    .filter(isActiveRecord)
    .map((item) => {
      const code = getField(item, "LegalEntityCode", "Legal Entity Code");
      const name = getField(item, "LegalEntityName", "Legal Entity Name", "Title");
      const countryCode = getField(item, "CountryCode", "Country Code");
      return mapOption(code, `${code || ""} | ${name || ""}`, {
        legalEntity: name,
        countryCode,
        country: getCountryNameFromCode(countryCode),
        currency: getField(item, "DefaultCurrency", "Default Currency"),
      });
    }));

  const locations = compactOptions(getArray(source, "locations", "Locations")
    .filter(isActiveRecord)
    .map((item) => {
      const code = getField(item, "LocationCode", "Location Code");
      const name = getField(item, "LocationName", "Location Name", "Title");
      const countryCode = getField(item, "CountryCode", "Country Code");
      return mapOption(code, `${code || ""} | ${name || ""}`, {
        locationName: name,
        countryCode,
        country: getCountryNameFromCode(countryCode),
        legalEntityCode: getField(item, "LegalEntityCode", "Legal Entity Code"),
        regionState: getField(item, "RegionState", "Region State"),
        timeZone: getField(item, "TimeZone", "Time Zone"),
      });
    }));

  const departments = compactOptions(getArray(source, "departments", "Departments")
    .filter(isActiveRecord)
    .map((item) => {
      const code = getField(item, "DepartmentCode", "Department Code");
      const name = getField(item, "DepartmentName", "Department Name", "Title");
      return mapOption(code, `${code || ""} | ${name || ""}`, {
        department: name,
        legalEntityCode: getField(item, "LegalEntityCode", "Legal Entity Code"),
      });
    }));

  const workUnits = compactOptions(getArray(source, "workUnits", "WorkUnits", "work units")
    .filter(isActiveRecord)
    .map((item) => {
      const code = getField(item, "WorkUnitCode", "Work Unit Code", "productionUnitCode");
      const name = getField(item, "WorkUnitName", "Work Unit Name", "Title");
      const countryCode = getField(item, "CountryCode", "Country Code");
      return mapOption(code, `${code || ""} | ${name || ""}`, {
        workUnitName: name,
        countryCode,
        country: getCountryNameFromCode(countryCode),
        locationCode: getField(item, "LocationCode", "Location Code"),
        departmentCode: getField(item, "DepartmentCode", "Department Code"),
        industryType: extractSharePointValue(getField(item, "IndustryType", "Industry Type")),
        workUnitType: extractSharePointValue(getField(item, "WorkUnitType", "Work Unit Type")),
        managerEmployeeCode: getField(item, "ManagerEmployeeCode", "Manager Employee Code"),
      });
    }));

  const employmentTypes = compactOptions(getArray(source, "employmentTypes", "EmploymentTypes", "employment types")
    .filter(isActiveRecord)
    .map((item) => {
      const name = getField(item, "EmploymentTypeName", "Employment Type Name", "Title");
      return mapOption(name, name, {
        employmentTypeCode: getField(item, "EmploymentTypeCode", "Employment Type Code"),
        countryCode: getField(item, "CountryCode", "Country Code") || "ALL",
        defaultFTEPercent: getField(item, "DefaultFTEPercent", "Default FTE Percent") || "100",
        isShiftEligible: getField(item, "IsShiftEligible", "Is Shift Eligible"),
      });
    }));

  const attendancePolicies = compactOptions(getArray(source, "attendancePolicies", "AttendancePolicies", "attendance policies")
    .filter(isActiveRecord)
    .map((item) => {
      const id = getField(item, "AttendancePolicyId", "AttendancePolicyID", "Attendance Policy ID", "Attendance Policy Id");
      const name = getField(item, "PolicyName", "Policy Name", "Title");
      const countryCode = getField(item, "CountryCode", "Country Code");
      return mapOption(id, `${id || ""} | ${name || ""}`, {
        countryCode,
        country: getCountryNameFromCode(countryCode),
        legalEntityCode: getField(item, "LegalEntityCode", "Legal Entity Code"),
        locationCode: getField(item, "LocationCode", "Location Code"),
        industryType: extractSharePointValue(getField(item, "IndustryType", "Industry Type")),
        employmentTypes: splitMultiValue(getField(item, "EmploymentType", "Employment Type")),
        isDefault: getField(item, "IsDefault", "Is Default"),
      });
    }));

  const leavePolicies = compactOptions(getArray(source, "leavePolicies", "LeavePolicies", "leave policies")
    .filter(isActiveRecord)
    .map((item) => {
      const id = getField(item, "LeavePolicyId", "LeavePolicyID", "Leave Policy ID", "Leave Policy Id");
      const name = getField(item, "PolicyName", "Policy Name", "Title");
      const countryCode = getField(item, "CountryCode", "Country Code");
      return mapOption(id, `${id || ""} | ${name || ""}`, {
        countryCode,
        country: getCountryNameFromCode(countryCode),
        legalEntityCode: getField(item, "LegalEntityCode", "Legal Entity Code"),
        locationCode: getField(item, "LocationCode", "Location Code"),
        industryType: extractSharePointValue(getField(item, "IndustryType", "Industry Type")),
        employmentTypes: splitMultiValue(getField(item, "EmploymentType", "Employment Type")),
        isDefault: getField(item, "IsDefault", "Is Default"),
        allowOverride: getField(item, "AllowOverride", "Allow Override", "AllowOverride "),
      });
    }));

  const shiftPatterns = compactOptions(getArray(source, "shiftPatterns", "ShiftPatterns", "shift patterns")
    .filter(isActiveRecord)
    .map((item) => {
      const id = getField(item, "ShiftPatternId", "ShiftPatternID", "Shift Pattern ID", "Shift Pattern Id");
      const name = getField(item, "ShiftName", "Shift Name", "Title");
      const countryCode = getField(item, "CountryCode", "Country Code");
      return mapOption(id, `${id || ""} | ${name || ""}`, {
        countryCode,
        country: getCountryNameFromCode(countryCode),
        locationCode: getField(item, "LocationCode", "Location Code"),
        industryType: extractSharePointValue(getField(item, "IndustryType", "Industry Type")),
        employmentTypes: splitMultiValue(getField(item, "EmploymentType", "Employment Type")),
        workUnitCode: getField(item, "WorkUnitCode", "Work Unit Code"),
        displayOrder: Number(getField(item, "DisplayOrder", "Display Order") || 999),
      });
    }))
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const weeklyOffPatterns = compactOptions(getArray(source, "weeklyOffPatterns", "WeeklyOffPatterns", "weekly off patterns")
    .filter(isActiveRecord)
    .map((item) => {
      const id = getField(item, "WeeklyOffPatternId", "WeeklyOffPatternID", "Weekly Off Pattern ID", "Weekly Off Pattern Id");
      const name = getField(item, "PatternName", "Pattern Name", "Title");
      const countryCode = getField(item, "CountryCode", "Country Code");
      return mapOption(id, `${id || ""} | ${name || ""}`, {
        countryCode,
        country: getCountryNameFromCode(countryCode),
        locationCode: getField(item, "LocationCode", "Location Code"),
        industryType: extractSharePointValue(getField(item, "IndustryType", "Industry Type")),
        employmentTypes: splitMultiValue(getField(item, "EmploymentType", "Employment Type")),
        workUnitCode: getField(item, "WorkUnitCode", "Work Unit Code"),
        weeklyOffType: extractSharePointValue(getField(item, "WeeklyOffType", "Weekly Off Type")),
        fixedDays: extractSharePointValue(getField(item, "FixedDays", "Fixed Days")),
        isRosterDriven: getField(item, "IsRosterDriven", "Is Roster Driven"),
        displayOrder: Number(getField(item, "DisplayOrder", "Display Order") || 999),
      });
    }))
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const holidayCalendars = compactOptions(getArray(source, "holidayCalendars", "HolidayCalendars", "holiday calendars")
    .filter(isActiveRecord)
    .map((item) => {
      const id = getField(item, "HolidayCalendarId", "HolidayCalendarID", "Holiday Calendar ID", "Holiday Calendar Id");
      const name = getField(item, "CalendarName", "Calendar Name", "Title");
      const countryCode = getField(item, "CountryCode", "Country Code");
      return mapOption(id, `${id || ""} | ${name || ""}`, {
        countryCode,
        country: getCountryNameFromCode(countryCode),
        legalEntityCode: getField(item, "LegalEntityCode", "Legal Entity Code"),
        locationCode: getField(item, "LocationCode", "Location Code"),
        year: getField(item, "Year"),
        isDefault: getField(item, "IsDefault", "Is Default"),
      });
    }));

  const employeePolicyAssignments = getArray(source, "employeePolicyAssignments", "EmployeePolicyAssignments", "employee policy assignments")
    .map(normalizeAssignmentRecord)
    .map(sanitizePolicyAssignmentIds);

  return {
    ...emptyMasterData,
    countries,
    legalEntities,
    locations,
    departments,
    workUnits,
    employmentTypes,
    attendancePolicies,
    leavePolicies,
    leavePolicyRules: getArray(source, "leavePolicyRules", "LeavePolicyRules", "leave policy rules"),
    shiftPatterns,
    weeklyOffPatterns,
    holidayCalendars,
    holidayCalendarDates: getArray(source, "holidayCalendarDates", "HolidayCalendarDates", "holiday calendar dates"),
    roleScopeAssignments: getArray(source, "roleScopeAssignments", "RoleScopeAssignments", "role scope assignments"),
    employeePolicyAssignments,
  };
}

function matchesCountry(item, country) {
  const countryCode = getCountryCodeFromName(country);
  const itemCountryCode = normalizeText(item.countryCode).toUpperCase();
  const selectedCode = normalizeText(countryCode).toUpperCase();
  const selectedName = normalizeText(country);
  return (
    !itemCountryCode ||
    itemCountryCode === selectedCode ||
    itemCountryCode === "ALL" ||
    itemCountryCode === "GLB" ||
    normalizeCompare(item.country) === normalizeCompare(selectedName)
  );
}

function normalizeCompare(value) {
  return normalizeText(value).toLowerCase().replace(/[^a-z0-9]/g, "");
}

function getEmploymentTypeTokens(employmentType, masterDataForLookup = emptyMasterData) {
  const selected = normalizeText(employmentType);
  const tokens = new Set([selected, normalizeCompare(selected)]);

  masterDataForLookup.employmentTypes.forEach((item) => {
    const optionName = normalizeText(item.value);
    const optionCode = normalizeText(item.employmentTypeCode);
    if (!selected) return;
    if (optionName === selected || normalizeCompare(optionName) === normalizeCompare(selected) || optionCode === selected) {
      if (optionName) {
        tokens.add(optionName);
        tokens.add(normalizeCompare(optionName));
      }
      if (optionCode) {
        tokens.add(optionCode);
        tokens.add(normalizeCompare(optionCode));
      }
    }
  });

  return [...tokens].filter(Boolean);
}

function matchesEmploymentType(item, employmentType, masterDataForLookup = emptyMasterData) {
  if (!item.employmentTypes || item.employmentTypes.length === 0) return true;
  const itemTokens = item.employmentTypes.flatMap((value) => [normalizeText(value), normalizeCompare(value)]);
  if (itemTokens.includes("All") || itemTokens.includes("ALL") || itemTokens.includes("all")) return true;
  const selectedTokens = getEmploymentTypeTokens(employmentType, masterDataForLookup);
  return selectedTokens.some((token) => itemTokens.includes(token));
}

function matchesIndustry(item, industryType) {
  const selected = normalizeText(industryType);
  const selectedKey = normalizeCompare(selected);
  const itemIndustries = splitMultiValue(item.industryType);
  if (itemIndustries.length === 0) return true;
  return itemIndustries.some((industry) => {
    const industryText = normalizeText(industry);
    const industryKey = normalizeCompare(industryText);
    return (
      industryText === "All" ||
      industryText === "ALL" ||
      industryText === selected ||
      industryKey === selectedKey ||
      (industryKey && selectedKey && (industryKey.includes(selectedKey) || selectedKey.includes(industryKey)))
    );
  });
}

function matchesOptionalField(itemValue, selectedValue) {
  const itemText = extractSharePointValue(itemValue);
  const selectedText = extractSharePointValue(selectedValue);
  return !itemText || !selectedText || itemText === selectedText;
}
function sameOrAllPayScale(value, selectedValue) {
  const itemText = normalizeText(extractSharePointValue(value));
  const selectedText = normalizeText(extractSharePointValue(selectedValue));
  const itemKey = normalizeCompare(itemText);
  const selectedKey = normalizeCompare(selectedText);
  return !itemKey || itemKey === "all" || itemKey === "global" || !selectedKey || itemKey === selectedKey;
}
function toNumber(value, fallback = 0) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  const numericCandidate = normalizeText(value).replace(/[^0-9.-]/g, "");
  const numberValue = Number(numericCandidate || value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}
function formatMoney(value, currency = "INR") {
  if (value === undefined || value === null || value === "") return "-";
  const amount = toNumber(value, 0);
  const safeCurrency = normalizeText(currency) || "INR";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: safeCurrency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${safeCurrency} ${amount.toLocaleString("en-IN")}`;
  }
}
function isPayScaleBandActive(band = {}) {
  return band.isActive !== false && band.IsActive !== false;
}
function matchesPayScaleBandForEmployeeForm(band = {}, formData = {}) {
  const selectedCountryCode = formData.countryCode || getCountryCodeFromName(formData.country);
  return isPayScaleBandActive(band) &&
    sameOrAllPayScale(band.countryCode || band.CountryCode, selectedCountryCode) &&
    sameOrAllPayScale(band.legalEntityCode || band.LegalEntityCode, formData.legalEntityCode) &&
    sameOrAllPayScale(band.industryType || band.IndustryType, formData.industryType) &&
    sameOrAllPayScale(band.employmentType || band.EmploymentType, formData.employmentType) &&
    sameOrAllPayScale(band.currency || band.Currency, formData.salaryCurrency);
}
function buildPayGradeOptionLabel(band = {}) {
  const code = normalizeText(band.payGradeCode || band.PayGradeCode || band.Title);
  const gradeName = normalizeText(band.gradeName || band.GradeName);
  const currency = normalizeText(band.currency || band.Currency) || "INR";
  const min = formatMoney(band.minAnnualCTC ?? band.MinAnnualCTC, currency);
  const mid = formatMoney(band.midAnnualCTC ?? band.MidAnnualCTC, currency);
  const max = formatMoney(band.maxAnnualCTC ?? band.MaxAnnualCTC, currency);
  return `${code}${gradeName ? ` | ${gradeName}` : ""} | ${min} - ${mid} - ${max}`;
}

function isLeavePolicyId(value) {
  const text = normalizeText(value).toUpperCase();
  return text.startsWith("LVPOL-") || text.startsWith("LPOL-");
}

function isAttendancePolicyId(value) {
  const text = normalizeText(value).toUpperCase();
  return text.startsWith("ATTPOL-") || text.startsWith("ATT-");
}

function isActiveAssignmentCurrent(record = {}) {
  const value = getField(record, "IsCurrent", "isCurrent", "Current");
  return value === true || value === "Yes" || value === "yes" || value === "true" || value === "True" || value === 1 || value === "1";
}

function normalizeAssignmentRecord(record = {}) {
  return {
    id: String(getField(record, "id", "ID", "ItemInternalId") || ""),
    ID: String(getField(record, "ID", "id", "ItemInternalId") || ""),
    employeeCode: normalizeText(getField(record, "EmployeeCode", "employeeCode")),
    leavePolicyId: normalizeText(getField(record, "LeavePolicyId", "leavePolicyId")),
    attendancePolicyId: normalizeText(getField(record, "AttendancePolicyId", "attendancePolicyId")),
    shiftPatternId: normalizeText(getField(record, "ShiftPatternId", "shiftPatternId", "ShiftPolicyId", "shiftPolicyId")),
    weeklyOffPatternId: normalizeText(getField(record, "WeeklyOffPatternId", "weeklyOffPatternId")),
    holidayCalendarId: normalizeText(getField(record, "HolidayCalendarId", "holidayCalendarId")),
    legalEntityCode: normalizeText(getField(record, "LegalEntityCode", "legalEntityCode")),
    locationCode: normalizeText(getField(record, "LocationCode", "locationCode")),
    departmentCode: normalizeText(getField(record, "DepartmentCode", "departmentCode")),
    workUnitCode: normalizeText(getField(record, "WorkUnitCode", "workUnitCode")),
    effectiveFrom: normalizeText(getField(record, "EffectiveFrom", "effectiveFrom")),
    effectiveTo: normalizeText(getField(record, "EffectiveTo", "effectiveTo")),
    isCurrent: isActiveAssignmentCurrent(record),
    isActive: isActiveRecord(record),
  };
}

function findCurrentAssignment(masterDataForLookup = emptyMasterData, employeeCode = "") {
  const code = normalizeText(employeeCode);
  if (!code) return null;
  return (masterDataForLookup.employeePolicyAssignments || []).find((assignment) => {
    return assignment.employeeCode === code && assignment.isCurrent && assignment.isActive;
  }) || null;
}

function sanitizePolicyAssignmentIds(assignment = {}) {
  if (!assignment) return assignment;
  const next = { ...assignment };
  if (next.leavePolicyId && isAttendancePolicyId(next.leavePolicyId)) {
    next.attendancePolicyId = next.attendancePolicyId || next.leavePolicyId;
    next.leavePolicyId = "";
  }
  if (next.attendancePolicyId && isLeavePolicyId(next.attendancePolicyId)) {
    next.leavePolicyId = next.leavePolicyId || next.attendancePolicyId;
    next.attendancePolicyId = "";
  }
  return next;
}


function firstValue(options) {
  return options[0]?.value || "";
}

function findOption(options, value) {
  return options.find((item) => item.value === value) || null;
}

function getDefaultFromOptions(options, currentValue) {
  if (currentValue && options.some((item) => item.value === currentValue)) return currentValue;
  const defaultOption = options.find((item) => item.isDefault === true || item.isDefault === "Yes" || item.isDefault === "yes");
  return defaultOption?.value || firstValue(options);
}


function getEmployeeField(employee, ...candidateNames) {
  return getField(employee, ...candidateNames);
}


function findOptionByValueOrText(options = [], ...values) {
  const normalizedValues = values
    .map((value) => normalizeCompare(extractSharePointValue(value)))
    .filter(Boolean);
  if (!normalizedValues.length) return null;
  return options.find((item) => {
    const candidates = [
      item.value,
      item.label,
      item.legalEntity,
      item.locationName,
      item.department,
      item.workUnitName,
      item.country,
      item.countryCode,
      item.legalEntityCode,
      item.locationCode,
      item.departmentCode,
      item.workUnitCode,
    ].map((value) => normalizeCompare(extractSharePointValue(value))).filter(Boolean);
    return normalizedValues.some((value) => candidates.includes(value));
  }) || null;
}

function ensureSelectedOption(options = [], selectedValue = "", selectedLabel = "Saved value") {
  const cleanValue = extractSharePointValue(selectedValue);
  if (!cleanValue) return options;
  if ((options || []).some((item) => extractSharePointValue(item.value) === cleanValue)) return options;
  return [mapOption(cleanValue, `${cleanValue} | ${selectedLabel}`), ...(options || [])];
}

function normalizeEmployeeRecordForEdit(employee = {}, masterDataForLookup = emptyMasterData) {
  const employeeId = getEmployeeField(employee, "employeeId", "EmpId", "EmployeeId", "EmployeeCode") || "";

  const rawCountry = extractSharePointValue(getEmployeeField(employee, "country", "Country"));
  const rawCountryCode = extractSharePointValue(getEmployeeField(employee, "countryCode", "CountryCode"));
  const countryCode = rawCountryCode || getCountryCodeFromName(rawCountry || "India");
  const country = rawCountry || getCountryNameFromCode(countryCode) || "India";

  const legalEntityText = extractSharePointValue(getEmployeeField(employee, "legalEntity", "LegalEntity", "legalEntityName", "LegalEntityName"));
  const rawLegalEntityCode = extractSharePointValue(getEmployeeField(employee, "legalEntityCode", "LegalEntityCode", "LegalEntityId", "legalEntityId"));
  const matchedLegalEntity = findOptionByValueOrText(masterDataForLookup.legalEntities, rawLegalEntityCode, legalEntityText);
  const legalEntityCode = rawLegalEntityCode || matchedLegalEntity?.value || "";
  const legalEntity = legalEntityText || matchedLegalEntity?.legalEntity || "";

  const rawLocationCode = extractSharePointValue(getEmployeeField(employee, "locationCode", "LocationCode", "workLocationId", "WorkLocationId"));
  const locationText = extractSharePointValue(getEmployeeField(employee, "location", "Location", "locationName", "LocationName"));
  const matchedLocation = findOptionByValueOrText(masterDataForLookup.locations, rawLocationCode, locationText);
  const locationCode = rawLocationCode || matchedLocation?.value || "";
  const location = locationText || matchedLocation?.locationName || "";

  const departmentText = extractSharePointValue(getEmployeeField(employee, "department", "Department", "departmentName", "DepartmentName"));
  const rawDepartmentCode = extractSharePointValue(getEmployeeField(employee, "departmentCode", "DepartmentCode"));
  const matchedDepartment = findOptionByValueOrText(masterDataForLookup.departments, rawDepartmentCode, departmentText);
  const departmentCode = rawDepartmentCode || matchedDepartment?.value || "";
  const department = departmentText || matchedDepartment?.department || "";

  const industryType = extractSharePointValue(getEmployeeField(employee, "industryType", "IndustryType")) || "Office / IT / Services";

  const rawWorkUnitCode = extractSharePointValue(getEmployeeField(employee, "workUnitCode", "WorkUnitCode", "productionUnitCode", "ProductionUnitCode"));
  const workUnitText = extractSharePointValue(getEmployeeField(employee, "workUnit", "WorkUnit", "workUnitName", "WorkUnitName", "productionUnit", "ProductionUnit"));
  const matchedWorkUnit = findOptionByValueOrText(masterDataForLookup.workUnits, rawWorkUnitCode, workUnitText);
  const workUnitCode = rawWorkUnitCode || matchedWorkUnit?.value || "";

  const shiftPatternId = extractSharePointValue(getEmployeeField(employee, "shiftPatternId", "ShiftPatternId", "shiftPolicyId", "ShiftPolicyId")) || "";

  return {
    ...initialFormData,
    id: String(getEmployeeField(employee, "id", "ID") || ""),
    ID: String(getEmployeeField(employee, "ID", "id") || ""),
    employeeId,
    workEmail: extractSharePointValue(getEmployeeField(employee, "workEmail", "WorkEmail")) || "",
    country,
    countryCode,
    legalEntity,
    legalEntityCode,
    regionState: extractSharePointValue(getEmployeeField(employee, "regionState", "RegionState")) || matchedLocation?.regionState || "",
    location,
    locationCode,
    workLocationId: locationCode,
    industryType,
    department,
    departmentCode,
    productionUnitCode: workUnitCode,
    workUnitCode,
    designation: extractSharePointValue(getEmployeeField(employee, "designation", "Designation")) || "",
    gradeLevel: extractSharePointValue(getEmployeeField(employee, "gradeLevel", "GradeLevel")) || "",
    reportingManager: extractSharePointValue(getEmployeeField(employee, "reportingManager", "ReportingManager")) || "",
    managerEmployeeCode: extractSharePointValue(getEmployeeField(employee, "managerEmployeeCode", "ManagerEmployeeCode")) || matchedWorkUnit?.managerEmployeeCode || "",
    joiningDate: extractSharePointValue(getEmployeeField(employee, "joiningDate", "JoiningDate")) || "",
    employmentStatus: extractSharePointValue(getEmployeeField(employee, "employmentStatus", "EmploymentStatus", "Status")) || "Offered",
    employmentType: extractSharePointValue(getEmployeeField(employee, "employmentType", "EmploymentType")) || "Full Time",
    workArrangement: extractSharePointValue(getEmployeeField(employee, "workArrangement", "WorkArrangement")) || "Office",
    ftePercent: String(extractSharePointValue(getEmployeeField(employee, "ftePercent", "FTEPercent")) || "100"),
    timeZone: extractSharePointValue(getEmployeeField(employee, "timeZone", "TimeZone")) || matchedLocation?.timeZone || "Asia/Kolkata",
    leavePolicyId: extractSharePointValue(getEmployeeField(employee, "leavePolicyId", "LeavePolicyId")) || "",
    attendancePolicyId: extractSharePointValue(getEmployeeField(employee, "attendancePolicyId", "AttendancePolicyId")) || "",
    shiftPatternId,
    shiftPolicyId: shiftPatternId,
    weeklyOffPatternId: extractSharePointValue(getEmployeeField(employee, "weeklyOffPatternId", "WeeklyOffPatternId")) || "",
    holidayCalendarId: extractSharePointValue(getEmployeeField(employee, "holidayCalendarId", "HolidayCalendarId")) || "",
    payGrade: extractSharePointValue(getEmployeeField(employee, "payGrade", "PayGrade")) || "",
    salaryCurrency: extractSharePointValue(getEmployeeField(employee, "salaryCurrency", "SalaryCurrency")) || matchedLegalEntity?.currency || "INR",
    compensationStatus: extractSharePointValue(getEmployeeField(employee, "compensationStatus", "CompensationStatus")) || "Pending Entry",
    currentEmployeeCTCId: extractSharePointValue(getEmployeeField(employee, "currentEmployeeCTCId", "CurrentEmployeeCTCId")) || "",
    currentCTCStructureId: extractSharePointValue(getEmployeeField(employee, "currentCTCStructureId", "CurrentCTCStructureId")) || "",
    currentCTCEffectiveFrom: extractSharePointValue(getEmployeeField(employee, "currentCTCEffectiveFrom", "CurrentCTCEffectiveFrom")) || "",
    currentCTCStatus: extractSharePointValue(getEmployeeField(employee, "currentCTCStatus", "CurrentCTCStatus")) || "",
    annualCTC: extractSharePointValue(getEmployeeField(employee, "annualCTC", "AnnualCTC")) || "",
    monthlyCTC: extractSharePointValue(getEmployeeField(employee, "monthlyCTC", "MonthlyCTC")) || "",
    monthlyGross: extractSharePointValue(getEmployeeField(employee, "monthlyGross", "MonthlyGross")) || "",
    annualEmployerCost: extractSharePointValue(getEmployeeField(employee, "annualEmployerCost", "AnnualEmployerCost")) || "",
    netPayEstimateMonthly: extractSharePointValue(getEmployeeField(employee, "netPayEstimateMonthly", "NetPayEstimateMonthly")) || "",
    cTCLastApprovedOn: extractSharePointValue(getEmployeeField(employee, "cTCLastApprovedOn", "CTCLastApprovedOn")) || "",
    cTCRevisionNumber: extractSharePointValue(getEmployeeField(employee, "cTCRevisionNumber", "CTCRevisionNumber")) || "",
    revisionDueFlag: getEmployeeField(employee, "revisionDueFlag", "RevisionDueFlag") === true || getEmployeeField(employee, "revisionDueFlag", "RevisionDueFlag") === "Yes" || getEmployeeField(employee, "revisionDueFlag", "RevisionDueFlag") === "true",
    profileCompletionStatus: extractSharePointValue(getEmployeeField(employee, "profileCompletionStatus", "ProfileCompletionStatus")) || "Not Started",
    profileVerificationStatus: extractSharePointValue(getEmployeeField(employee, "profileVerificationStatus", "ProfileVerificationStatus")) || "Not Verified",
    onboardingStage: extractSharePointValue(getEmployeeField(employee, "onboardingStage", "OnboardingStage")) || "Offered",
  };
}
function mergeCurrentAssignmentIntoForm(formData, assignment, masterDataForLookup = emptyMasterData) {
  const masterAssignment = findCurrentAssignment(masterDataForLookup, formData.employeeId);
  const safeAssignment = sanitizePolicyAssignmentIds(masterAssignment || assignment);
  if (!safeAssignment) return formData;
  const legalEntity = masterDataForLookup.legalEntities.find((item) => item.value === safeAssignment.legalEntityCode);
  const location = masterDataForLookup.locations.find((item) => item.value === safeAssignment.locationCode);
  const department = masterDataForLookup.departments.find((item) => item.value === safeAssignment.departmentCode);
  return {
    ...formData,
    legalEntityCode: safeAssignment.legalEntityCode || formData.legalEntityCode,
    legalEntity: legalEntity?.legalEntity || formData.legalEntity,
    locationCode: safeAssignment.locationCode || formData.locationCode,
    workLocationId: safeAssignment.locationCode || formData.workLocationId,
    regionState: location?.regionState || formData.regionState,
    departmentCode: safeAssignment.departmentCode || formData.departmentCode,
    department: department?.department || formData.department,
    productionUnitCode: safeAssignment.workUnitCode || formData.productionUnitCode,
    workUnitCode: safeAssignment.workUnitCode || formData.workUnitCode || formData.productionUnitCode,
    leavePolicyId: safeAssignment.leavePolicyId || formData.leavePolicyId,
    attendancePolicyId: safeAssignment.attendancePolicyId || formData.attendancePolicyId,
    shiftPatternId: safeAssignment.shiftPatternId || formData.shiftPatternId,
    shiftPolicyId: safeAssignment.shiftPatternId || formData.shiftPolicyId,
    weeklyOffPatternId: safeAssignment.weeklyOffPatternId || formData.weeklyOffPatternId,
    holidayCalendarId: safeAssignment.holidayCalendarId || formData.holidayCalendarId,
  };
}

function EmployeeMaster() {
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [errors, setErrors] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [masterData, setMasterData] = useState(emptyMasterData);
  const [payScaleBands, setPayScaleBands] = useState([]);
  const [loadingPayScaleBands, setLoadingPayScaleBands] = useState(false);
  const [loadingMasterData, setLoadingMasterData] = useState(false);
  const [policyHistoryEmployeeId, setPolicyHistoryEmployeeId] = useState("");
  const [policyHistoryRows, setPolicyHistoryRows] = useState([]);
  const [loadingPolicyHistory, setLoadingPolicyHistory] = useState(false);

  const currentCountry = formData.country || "India";
  const currentEmploymentType = formData.employmentType || "Full Time";

  const countryOptions = useMemo(() => {
    return masterData.countries.length ? masterData.countries.map((item) => item.value) : FALLBACK_COUNTRY_OPTIONS;
  }, [masterData.countries]);

  const legalEntityOptions = useMemo(() => {
    return masterData.legalEntities.filter((item) => matchesCountry(item, currentCountry));
  }, [masterData.legalEntities, currentCountry]);

  const locationOptions = useMemo(() => {
    return masterData.locations.filter((item) => matchesCountry(item, currentCountry) && matchesOptionalField(item.legalEntityCode, formData.legalEntityCode));
  }, [masterData.locations, currentCountry, formData.legalEntityCode]);

  const departmentOptions = useMemo(() => masterData.departments, [masterData.departments]);

  const workUnitOptions = useMemo(() => {
    const baseMatches = (item) =>
      matchesCountry(item, currentCountry) &&
      matchesOptionalField(item.locationCode, formData.locationCode) &&
      matchesIndustry(item, formData.industryType);

    const exactDepartmentMatches = masterData.workUnits.filter((item) => {
      return baseMatches(item) && matchesOptionalField(item.departmentCode, formData.departmentCode);
    });

    if (exactDepartmentMatches.length > 0) return exactDepartmentMatches;

    const locationIndustryMatches = masterData.workUnits.filter(baseMatches);
    if (locationIndustryMatches.length > 0) return locationIndustryMatches;

    return masterData.workUnits.filter((item) => {
      return matchesCountry(item, currentCountry) && matchesIndustry(item, formData.industryType);
    });
  }, [masterData.workUnits, currentCountry, formData.locationCode, formData.departmentCode, formData.industryType]);
  const employmentTypeOptions = useMemo(() => {
    const options = masterData.employmentTypes.filter((item) => matchesCountry(item, currentCountry)).map((item) => item.value);
    return options.length ? [...new Set(options)] : FALLBACK_EMPLOYMENT_TYPES;
  }, [masterData.employmentTypes, currentCountry]);

  const industryTypeOptions = useMemo(() => {
    const values = new Set(FALLBACK_INDUSTRY_TYPES);
    masterData.workUnits.forEach((item) => addCleanOption(values, item.industryType));
    masterData.leavePolicies.forEach((item) => {
      if (normalizeText(item.industryType) !== "All") addCleanOption(values, item.industryType);
    });
    return [...values].filter((item) => item && !item.includes("@odata.type") && !item.includes("Microsoft.Azure.Connectors"));
  }, [masterData.workUnits, masterData.leavePolicies]);

  const timeZoneOptions = useMemo(() => {
    const values = new Set(FALLBACK_TIME_ZONES);
    masterData.locations.forEach((item) => {
      if (item.timeZone) values.add(item.timeZone);
    });
    return [...values];
  }, [masterData.locations]);

  const leavePolicyOptions = useMemo(() => {
    return masterData.leavePolicies.filter((item) => {
      return matchesCountry(item, currentCountry) && matchesEmploymentType(item, currentEmploymentType, masterData) && matchesIndustry(item, formData.industryType);
    });
  }, [masterData.leavePolicies, currentCountry, currentEmploymentType, formData.industryType]);

  const attendancePolicyOptions = useMemo(() => {
    return masterData.attendancePolicies.filter((item) => {
      return matchesCountry(item, currentCountry) && matchesEmploymentType(item, currentEmploymentType, masterData) && matchesIndustry(item, formData.industryType);
    });
  }, [masterData.attendancePolicies, currentCountry, currentEmploymentType, formData.industryType]);

  const shiftPatternOptions = useMemo(() => {
    return masterData.shiftPatterns.filter((item) => {
      return (
        matchesCountry(item, currentCountry) &&
        matchesEmploymentType(item, currentEmploymentType, masterData) &&
        matchesIndustry(item, formData.industryType) &&
        matchesOptionalField(item.locationCode, formData.locationCode) &&
        matchesOptionalField(item.workUnitCode, formData.productionUnitCode)
      );
    });
  }, [masterData.shiftPatterns, currentCountry, currentEmploymentType, formData.industryType, formData.locationCode, formData.productionUnitCode]);

  const weeklyOffPatternOptions = useMemo(() => {
    return masterData.weeklyOffPatterns.filter((item) => {
      return (
        matchesCountry(item, currentCountry) &&
        matchesEmploymentType(item, currentEmploymentType, masterData) &&
        matchesIndustry(item, formData.industryType) &&
        matchesOptionalField(item.locationCode, formData.locationCode) &&
        matchesOptionalField(item.workUnitCode, formData.productionUnitCode)
      );
    });
  }, [masterData.weeklyOffPatterns, currentCountry, currentEmploymentType, formData.industryType, formData.locationCode, formData.productionUnitCode]);

  const holidayCalendarOptions = useMemo(() => {
    return masterData.holidayCalendars.filter((item) => {
      return matchesCountry(item, currentCountry) && matchesOptionalField(item.legalEntityCode, formData.legalEntityCode) && matchesOptionalField(item.locationCode, formData.locationCode);
    });
  }, [masterData.holidayCalendars, currentCountry, formData.legalEntityCode, formData.locationCode]);
  const filteredPayScaleBands = useMemo(() => {
    return (payScaleBands || [])
      .filter((band) => matchesPayScaleBandForEmployeeForm(band, formData))
      .sort((a, b) => {
        const orderA = toNumber(a.displayOrder ?? a.DisplayOrder, 999);
        const orderB = toNumber(b.displayOrder ?? b.DisplayOrder, 999);
        return orderA - orderB || normalizeText(a.payGradeCode || a.PayGradeCode).localeCompare(normalizeText(b.payGradeCode || b.PayGradeCode));
      });
  }, [payScaleBands, formData.countryCode, formData.country, formData.legalEntityCode, formData.industryType, formData.employmentType, formData.salaryCurrency]);
  const payGradeOptions = useMemo(() => {
    const options = filteredPayScaleBands.map((band) => {
      const payGradeCode = normalizeText(band.payGradeCode || band.PayGradeCode || band.Title);
      return mapOption(payGradeCode, buildPayGradeOptionLabel(band), { band });
    });
    if (formData.payGrade && !options.some((option) => option.value === formData.payGrade)) {
      options.unshift(mapOption(formData.payGrade, `${formData.payGrade} | Not available for current selection`, { missingBand: true }));
    }
    return compactOptions(options);
  }, [filteredPayScaleBands, formData.payGrade]);
  const selectedPayScaleBand = useMemo(() => {
    return filteredPayScaleBands.find((band) => normalizeText(band.payGradeCode || band.PayGradeCode || band.Title) === normalizeText(formData.payGrade)) || null;
  }, [filteredPayScaleBands, formData.payGrade]);

  const applyRecommendedAssignments = (nextFormData, dataForFiltering = masterData) => {
    const nextCountry = nextFormData.country || "India";
    const nextEmploymentType = nextFormData.employmentType || "Full Time";
    const filteredLeavePolicies = dataForFiltering.leavePolicies.filter((item) => matchesCountry(item, nextCountry) && matchesEmploymentType(item, nextEmploymentType, dataForFiltering) && matchesIndustry(item, nextFormData.industryType));
    const filteredAttendancePolicies = dataForFiltering.attendancePolicies.filter((item) => matchesCountry(item, nextCountry) && matchesEmploymentType(item, nextEmploymentType, dataForFiltering) && matchesIndustry(item, nextFormData.industryType));
    const filteredShiftPatterns = dataForFiltering.shiftPatterns.filter((item) => matchesCountry(item, nextCountry) && matchesEmploymentType(item, nextEmploymentType, dataForFiltering) && matchesIndustry(item, nextFormData.industryType) && matchesOptionalField(item.locationCode, nextFormData.locationCode) && matchesOptionalField(item.workUnitCode, nextFormData.productionUnitCode));
    const filteredWeeklyOffPatterns = dataForFiltering.weeklyOffPatterns.filter((item) => matchesCountry(item, nextCountry) && matchesEmploymentType(item, nextEmploymentType, dataForFiltering) && matchesIndustry(item, nextFormData.industryType) && matchesOptionalField(item.locationCode, nextFormData.locationCode) && matchesOptionalField(item.workUnitCode, nextFormData.productionUnitCode));
    const filteredHolidayCalendars = dataForFiltering.holidayCalendars.filter((item) => matchesCountry(item, nextCountry) && matchesOptionalField(item.legalEntityCode, nextFormData.legalEntityCode) && matchesOptionalField(item.locationCode, nextFormData.locationCode));

    const shiftPatternId = getDefaultFromOptions(filteredShiftPatterns, nextFormData.shiftPatternId);

    return {
      ...nextFormData,
      leavePolicyId: getDefaultFromOptions(filteredLeavePolicies, nextFormData.leavePolicyId),
      attendancePolicyId: getDefaultFromOptions(filteredAttendancePolicies, nextFormData.attendancePolicyId),
      shiftPatternId,
      shiftPolicyId: shiftPatternId,
      weeklyOffPatternId: getDefaultFromOptions(filteredWeeklyOffPatterns, nextFormData.weeklyOffPatternId),
      holidayCalendarId: getDefaultFromOptions(filteredHolidayCalendars, nextFormData.holidayCalendarId),
    };
  };

  const loadEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const rows = await getEmployeeMasters();
      setEmployees(rows || []);
    } catch (error) {
      console.error("Load employees error:", error);
      setMessage(error.message || "Failed to load employee records.");
      setMessageType("error");
    } finally {
      setLoadingEmployees(false);
    }
  };

  const loadMasterData = async () => {
    try {
      setLoadingMasterData(true);
      const data = await getHRSettingsMasterData();
      const normalizedData = normalizeMasterData(data);
      setMasterData(normalizedData);
      setFormData((previous) => editingEmployeeId ? previous : applyRecommendedAssignments(previous, normalizedData));
    } catch (error) {
      console.error("Load HR settings master data error:", error);
      setMessage(error.message || "Failed to load HR settings master data. Using fallback employee form options.");
      setMessageType("error");
    } finally {
      setLoadingMasterData(false);
    }
  };
  const loadPayScaleBands = async () => {
    try {
      setLoadingPayScaleBands(true);
      const rows = await getPayScaleBands({ includeInactive: false }, { userRole: "HRAdmin", sourceModule: "EmployeeMaster" });
      setPayScaleBands(rows || []);
    } catch (error) {
      console.error("Load PayScaleBands error:", error);
      setMessage(error.message || "Failed to load PayScaleBands. PayGrade dropdown may be empty.");
      setMessageType("error");
    } finally {
      setLoadingPayScaleBands(false);
    }
  };

  useEffect(() => {
    loadEmployees();
    loadMasterData();
    loadPayScaleBands();
  }, []);
  useEffect(() => {
    if (!payScaleBands.length || !formData.payGrade) return;
    const stillAvailable = filteredPayScaleBands.some((band) => normalizeText(band.payGradeCode || band.PayGradeCode || band.Title) === normalizeText(formData.payGrade));
    if (!stillAvailable && !editingEmployeeId) {
      setFormData((previous) => ({ ...previous, payGrade: "" }));
    }
  }, [filteredPayScaleBands, payScaleBands.length, formData.payGrade, editingEmployeeId]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      let updated = {
        ...prev,
        [name]: value,
      };

      if (name === "country") {
        const selectedEntity = masterData.legalEntities.find((item) => item.country === value);
        const selectedLocation = masterData.locations.find((item) => item.country === value && (!selectedEntity || item.legalEntityCode === selectedEntity.value));
        updated = {
          ...updated,
          country: value,
          countryCode: getCountryCodeFromName(value),
          legalEntity: selectedEntity?.legalEntity || "",
          legalEntityCode: selectedEntity?.value || "",
          salaryCurrency: selectedEntity?.currency || prev.salaryCurrency,
          locationCode: selectedLocation?.value || "",
          workLocationId: selectedLocation?.value || "",
          regionState: selectedLocation?.regionState || "",
          timeZone: selectedLocation?.timeZone || prev.timeZone,
          productionUnitCode: "",
          workUnitCode: "",
        };
      }

      if (name === "legalEntityCode") {
        const selectedEntity = findOption(masterData.legalEntities, value);
        updated = {
          ...updated,
          legalEntity: selectedEntity?.legalEntity || prev.legalEntity,
          countryCode: selectedEntity?.countryCode || prev.countryCode,
          salaryCurrency: selectedEntity?.currency || prev.salaryCurrency,
        };
      }

      if (name === "locationCode") {
        const selectedLocation = findOption(masterData.locations, value);
        updated = {
          ...updated,
          locationCode: value,
          workLocationId: value,
          regionState: selectedLocation?.regionState || prev.regionState,
          timeZone: selectedLocation?.timeZone || prev.timeZone,
          productionUnitCode: "",
          workUnitCode: "",
        };
      }

      if (name === "departmentCode") {
        const selectedDepartment = findOption(masterData.departments, value);
        updated = {
          ...updated,
          departmentCode: value,
          department: selectedDepartment?.department || prev.department,
          productionUnitCode: "",
        };
      }

      if (name === "productionUnitCode" || name === "workUnitCode") {
        const selectedWorkUnit = findOption(masterData.workUnits, value);
        updated = {
          ...updated,
          productionUnitCode: value,
          workUnitCode: value,
          managerEmployeeCode: selectedWorkUnit?.managerEmployeeCode || prev.managerEmployeeCode,
        };
      }

      if (name === "employmentType") {
        updated = {
          ...updated,
          workArrangement: value === "Shift Worker" ? "Hourly Shift" : prev.workArrangement,
          ftePercent: value === "Part Time" ? "50" : prev.ftePercent,
        };
      }

      if (name === "shiftPatternId") {
        updated = {
          ...updated,
          shiftPatternId: value,
          shiftPolicyId: value,
        };
      }

      if (["country", "legalEntityCode", "locationCode", "departmentCode", "productionUnitCode", "industryType", "employmentType"].includes(name)) {
        updated = applyRecommendedAssignments(updated);
      }
      if (["country", "legalEntityCode", "industryType", "employmentType", "salaryCurrency"].includes(name)) {
        const matchingCurrentPayGrade = (payScaleBands || []).some((band) =>
          matchesPayScaleBandForEmployeeForm(band, updated) && normalizeText(band.payGradeCode || band.PayGradeCode || band.Title) === normalizeText(updated.payGrade)
        );
        if (updated.payGrade && !matchingCurrentPayGrade && !editingEmployeeId) {
          updated = { ...updated, payGrade: "" };
        }
      }

      return updated;
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
    setMessage("");
    setMessageType("");
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.employeeId.trim()) newErrors.employeeId = "Employee ID is required";
    if (!formData.workEmail.trim()) {
      newErrors.workEmail = "Work Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.workEmail)) {
      newErrors.workEmail = "Enter a valid work email";
    }
    if (!formData.legalEntityCode.trim()) newErrors.legalEntityCode = "Legal entity is required";
    if (!formData.country.trim()) newErrors.country = "Country is required";
    if (!formData.countryCode.trim()) newErrors.countryCode = "Country code is required";
    if (!formData.locationCode.trim()) newErrors.locationCode = "Location is required";
    if (!formData.industryType.trim()) newErrors.industryType = "Industry type is required";
    if (!formData.departmentCode.trim()) newErrors.departmentCode = "Department is required";
    if (!formData.productionUnitCode.trim()) newErrors.productionUnitCode = "Work Unit is required";
    if (!formData.designation.trim()) newErrors.designation = "Designation is required";
    if (!formData.gradeLevel.trim()) newErrors.gradeLevel = "Grade level is required";
    if (!formData.reportingManager.trim()) newErrors.reportingManager = "Reporting manager is required";
    if (!formData.joiningDate) newErrors.joiningDate = "Joining date is required";
    if (!formData.employmentStatus.trim()) newErrors.employmentStatus = "Employment status is required";
    if (!formData.employmentType.trim()) newErrors.employmentType = "Employment type is required";
    if (!formData.timeZone.trim()) newErrors.timeZone = "Time zone is required";
    if (!formData.leavePolicyId.trim()) newErrors.leavePolicyId = "Leave policy ID is required";
    if (!formData.attendancePolicyId.trim()) newErrors.attendancePolicyId = "Attendance policy ID is required";
    if (!formData.shiftPatternId.trim()) newErrors.shiftPatternId = "Shift pattern ID is required";
    if (!formData.weeklyOffPatternId.trim()) newErrors.weeklyOffPatternId = "Weekly off pattern ID is required";
    if (!formData.holidayCalendarId.trim()) newErrors.holidayCalendarId = "Holiday calendar ID is required";
    if (!formData.payGrade.trim()) newErrors.payGrade = "Pay Grade is required for CTC validation";
    if (formData.payGrade.trim() && payScaleBands.length > 0 && !selectedPayScaleBand) newErrors.payGrade = "Selected Pay Grade is not valid for current country / industry / employment type / currency";
    if (!formData.salaryCurrency.trim()) newErrors.salaryCurrency = "Salary currency is required";
    if (!formData.compensationStatus.trim()) newErrors.compensationStatus = "Compensation status is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setMessage("Please fix validation errors before saving.");
      setMessageType("error");
      return;
    }

    try {
      setSaving(true);
      setMessage("");
      setMessageType("");
      const result = await saveEmployeeMaster({
        ...formData,
        workUnitCode: formData.workUnitCode || formData.productionUnitCode,
        productionUnitCode: formData.productionUnitCode || formData.workUnitCode,
      });
      setMessage(result?.message || "Employee master saved successfully.");
      setMessageType("success");
      await loadEmployees();
      setEditingEmployeeId(null);
      setFormData(initialFormData);
      setErrors({});
    } catch (error) {
      console.error("Employee master save error:", error);
      setMessage(error.message || "Failed to save employee master.");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setFormData(initialFormData);
    setErrors({});
    setMessage("");
    setMessageType("");
    setEditingEmployeeId(null);
  };

  const handleEditEmployee = async (employee) => {
    const baseFormData = normalizeEmployeeRecordForEdit(employee, masterData);
    setEditingEmployeeId(baseFormData.id || baseFormData.ID || baseFormData.employeeId);
    setFormData(baseFormData);
    setMessage("");
    setMessageType("");

    try {
      const currentAssignment = await getCurrentEmployeePolicyAssignment(baseFormData.employeeId);
      if (currentAssignment) {
        const hydratedFormData = mergeCurrentAssignmentIntoForm(baseFormData, currentAssignment, masterData);
        setFormData(hydratedFormData);
      }
    } catch (error) {
      console.warn("Current assignment hydration skipped:", error);
      setMessage(error.message || "Could not load current policy assignment. EmployeeMaster data loaded only.");
      setMessageType("error");
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewPolicyHistory = async (employee) => {
    const employeeId = employee.employeeId || employee.EmpId || employee.EmployeeCode || "";
    if (!employeeId) return;
    setPolicyHistoryEmployeeId(employeeId);
    setPolicyHistoryRows([]);
    setLoadingPolicyHistory(true);
    try {
      const rows = await getEmployeePolicyHistory(employeeId);
      setPolicyHistoryRows(rows);
    } catch (error) {
      console.error("Load policy history error:", error);
      setMessage(error.message || "Failed to load policy assignment history.");
      setMessageType("error");
    } finally {
      setLoadingPolicyHistory(false);
    }
  };

  const renderError = (fieldName) => {
    if (!errors[fieldName]) return null;
    return <div style={errorStyle}>{errors[fieldName]}</div>;
  };

  const renderInput = ({ label, name, required = false, type = "text", placeholder = "", disabled = false }) => (
    <div style={fieldStyle}>
      <label style={labelStyle}>{label} {required && "*"}</label>
      <input
        name={name}
        type={type}
        value={formData[name] || ""}
        onChange={handleChange}
        style={{ ...inputStyle, backgroundColor: disabled ? "#f1f5f9" : "#ffffff" }}
        placeholder={placeholder}
        disabled={disabled}
      />
      {renderError(name)}
    </div>
  );

  const renderSelect = ({ label, name, options, required = false, disabled = false }) => {
    const safeOptionsBase = compactOptions((options || []).map((option) => {
      if (typeof option === "string") {
        const clean = extractSharePointValue(option);
        return mapOption(clean, clean);
      }
      const cleanValue = extractSharePointValue(option.value);
      const cleanLabel = extractSharePointValue(option.label || option.value);
      return mapOption(cleanValue, cleanLabel, { ...option, value: cleanValue, label: cleanLabel });
    })).filter((option) => option.value && !option.value.includes("@odata.type") && !option.value.includes("Microsoft.Azure.Connectors"));
    const safeOptions = ensureSelectedOption(safeOptionsBase, formData[name], "Saved value");
    return (
      <div style={fieldStyle}>
        <label style={labelStyle}>{label} {required && "*"}</label>
        <select
          name={name}
          value={formData[name] || ""}
          onChange={handleChange}
          style={{ ...inputStyle, backgroundColor: disabled ? "#f1f5f9" : "#ffffff" }}
          disabled={disabled}
        >
          <option value="">Select</option>
          {safeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {renderError(name)}
      </div>
    );
  };

  const renderPayScaleSummary = () => {
    if (loadingPayScaleBands) {
      return <div style={payScaleInfoStyle}>Loading PayGrade / PayScale bands...</div>;
    }
    if (!payScaleBands.length) {
      return <div style={payScaleWarningStyle}>No PayScale bands loaded. Create PayBands in Compensation Setup or check F_HR_CTC_MasterData_Unified getPayScaleBands.</div>;
    }
    if (!filteredPayScaleBands.length) {
      return <div style={payScaleWarningStyle}>No active PayGrade band matches current Country / Legal Entity / Industry Type / Employment Type / Currency.</div>;
    }
    if (!selectedPayScaleBand) {
      return (
        <div style={payScaleInfoStyle}>
          {filteredPayScaleBands.length} PayGrade option(s) available for current selection. Select Pay Grade to view min / mid / max scale.
        </div>
      );
    }
    const currency = selectedPayScaleBand.currency || selectedPayScaleBand.Currency || formData.salaryCurrency || "INR";
    return (
      <div style={payScaleBandCardStyle}>
        <div style={payScaleBandHeaderStyle}>
          <strong>{selectedPayScaleBand.payGradeCode || selectedPayScaleBand.PayGradeCode}</strong>
          <span>{selectedPayScaleBand.gradeName || selectedPayScaleBand.GradeName || "PayScale Band"}</span>
        </div>
        <div style={payScaleBandGridStyle}>
          <div><b>Min Annual CTC</b><br /><span>{formatMoney(selectedPayScaleBand.minAnnualCTC ?? selectedPayScaleBand.MinAnnualCTC, currency)}</span></div>
          <div><b>Mid Annual CTC</b><br /><span>{formatMoney(selectedPayScaleBand.midAnnualCTC ?? selectedPayScaleBand.MidAnnualCTC, currency)}</span></div>
          <div><b>Max Annual CTC</b><br /><span>{formatMoney(selectedPayScaleBand.maxAnnualCTC ?? selectedPayScaleBand.MaxAnnualCTC, currency)}</span></div>
          <div><b>Monthly Gross Range</b><br /><span>{formatMoney(selectedPayScaleBand.minMonthlyGross ?? selectedPayScaleBand.MinMonthlyGross, currency)} - {formatMoney(selectedPayScaleBand.maxMonthlyGross ?? selectedPayScaleBand.MaxMonthlyGross, currency)}</span></div>
        </div>
      </div>
    );
  };

  const messageBoxStyle = {
    ...messageStyle,
    borderColor: messageType === "error" ? "#fecaca" : "#bbf7d0",
    backgroundColor: messageType === "error" ? "#fef2f2" : "#f0fdf4",
    color: messageType === "error" ? "#991b1b" : "#166534",
  };

  return (
    <div className="fm-hr-page-shell" style={pageStyle}>
      <div className="fm-hr-hero" style={headerStyle}>
        <h1 style={{ margin: 0 }}>Employee Master</h1>
        <p style={{ marginTop: "8px", color: "rgba(255,255,255,0.88)" }}>
          Enterprise employee setup for identity, organization, work unit, employment arrangement, and attendance/leave policy assignment.
        </p>
      </div>

      {loadingMasterData && <div style={infoStyle}>Loading HR Settings master data...</div>}
      {message && <div style={messageBoxStyle}>{message}</div>}
      {editingEmployeeId && <div style={editingStyle}>Editing employee: {formData.employeeId}</div>}

      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>Identity</h2>
        <p style={noteStyle}>Employee ID is locked after creation. Work Email remains the employee identity reference for profile and login mapping.</p>
        <div style={gridStyle}>
          {renderInput({ label: "Employee ID", name: "employeeId", required: true, placeholder: "Example: EMP001", disabled: Boolean(editingEmployeeId) })}
          {renderInput({ label: "Work Email", name: "workEmail", required: true, type: "email", placeholder: "employee@fumind.ai" })}
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>Organization & Work Structure</h2>
        <p style={noteStyle}>Dynamic dropdowns are loaded from HR Settings SharePoint lists through Get_HR_Settings_MasterData.</p>
        <div style={gridStyle}>
                    {renderSelect({ label: "Country", name: "country", required: true, options: countryOptions })}
          {renderInput({ label: "Country Code", name: "countryCode", required: true })}
          {renderSelect({ label: "Legal Entity", name: "legalEntityCode", required: true, options: legalEntityOptions })}
          {renderInput({ label: "Region / State", name: "regionState", placeholder: "Andhra Pradesh / Bavaria" })}
          {renderSelect({ label: "Location", name: "locationCode", required: true, options: locationOptions })}
          {renderSelect({ label: "Industry Type", name: "industryType", required: true, options: industryTypeOptions })}
          {renderSelect({ label: "Department", name: "departmentCode", required: true, options: departmentOptions })}
          {renderSelect({ label: "Work Unit", name: "productionUnitCode", required: true, options: workUnitOptions })}
          {renderInput({ label: "Designation", name: "designation", required: true, placeholder: "Example: Senior Engineer" })}
          {renderSelect({ label: "Grade Level", name: "gradeLevel", required: true, options: ["L1", "L2", "L3", "L4", "L5", "CXO"] })}
          {renderInput({ label: "Reporting Manager", name: "reportingManager", required: true, placeholder: "manager@fumind.ai" })}
          {renderInput({ label: "Manager Employee Code", name: "managerEmployeeCode", placeholder: "Example: EMP010" })}
          {renderInput({ label: "Joining Date", name: "joiningDate", required: true, type: "date" })}
          {renderSelect({ label: "Employment Status", name: "employmentStatus", required: true, options: ["Offered", "Preboarding", "Onboarding", "Active", "Probation", "Notice Period", "Exited", "Inactive"] })}
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>Employment & Work Arrangement</h2>
        <p style={noteStyle}>Employment Type recommends defaults, but HR can override from valid SharePoint-backed options.</p>
        <div style={gridStyle}>
          {renderSelect({ label: "Employment Type", name: "employmentType", required: true, options: employmentTypeOptions })}
          {renderSelect({ label: "Work Arrangement", name: "workArrangement", options: WORK_ARRANGEMENT_OPTIONS })}
          {renderSelect({ label: "FTE %", name: "ftePercent", options: FTE_PERCENT_OPTIONS })}
          {renderSelect({ label: "Time Zone", name: "timeZone", required: true, options: timeZoneOptions })}
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>Policy Assignment - Attendance & Leave</h2>
        <p style={noteStyle}>Policy IDs are loaded from HR Settings using canonical fields: LeavePolicyId, AttendancePolicyId, ShiftPatternId, WeeklyOffPatternId and HolidayCalendarId. Options are filtered by country, employment type code/name, industry, location and work unit.</p>
        <div style={gridStyle}>
          {renderSelect({ label: "Leave Policy ID", name: "leavePolicyId", required: true, options: leavePolicyOptions })}
          {renderSelect({ label: "Attendance Policy ID", name: "attendancePolicyId", required: true, options: attendancePolicyOptions })}
          {renderSelect({ label: "Shift Pattern ID", name: "shiftPatternId", required: true, options: shiftPatternOptions })}
          {renderSelect({ label: "Weekly Off Pattern ID", name: "weeklyOffPatternId", required: true, options: weeklyOffPatternOptions })}
          {renderSelect({ label: "Holiday Calendar ID", name: "holidayCalendarId", required: true, options: holidayCalendarOptions })}
        </div>
        <div style={assignmentNoteStyle}>
          Governance: HRAdmin/Admin owns master setup. HR can assign policy IDs. Manager can later update only scoped operational fields such as Work Unit, Shift Pattern, and Weekly Off Pattern.
          <div style={{ marginTop: "8px" }}>
            Available now: Leave Policies {leavePolicyOptions.length}, Attendance Policies {attendancePolicyOptions.length}, Shift Patterns {shiftPatternOptions.length}, Weekly Off Patterns {weeklyOffPatternOptions.length}, Holiday Calendars {holidayCalendarOptions.length}.
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>Basic Compensation Reference</h2>
        <p style={noteStyle}>Pay Grade is now selected from HR_PayScaleBands based on Country, Legal Entity, Industry Type, Employment Type and Salary Currency. Full CTC, salary structure and revision history remain maintained separately in Compensation / CTC.</p>
        <div style={gridStyle}>
          {renderSelect({ label: "Pay Grade", name: "payGrade", required: true, options: payGradeOptions, disabled: loadingPayScaleBands })}
          {renderSelect({ label: "Salary Currency", name: "salaryCurrency", required: true, options: ["INR", "EUR", "USD", "GBP"] })}
          {renderSelect({ label: "Compensation Status", name: "compensationStatus", required: true, options: ["Not Started", "Pending Entry", "Pending Approval", "Approved", "Revision Due"] })}
        </div>
        {renderPayScaleSummary()}
      </div>
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>Current CTC Summary - Read Only</h2>
        <p style={noteStyle}>These fields are synced from the CTC approval flow. HR should not manually edit these values here.</p>
        <div style={gridStyle}>
          {renderInput({ label: "Current Employee CTC ID", name: "currentEmployeeCTCId", disabled: true })}
          {renderInput({ label: "Current CTC Structure ID", name: "currentCTCStructureId", disabled: true })}
          {renderInput({ label: "Current CTC Effective From", name: "currentCTCEffectiveFrom", type: "date", disabled: true })}
          {renderInput({ label: "Current CTC Status", name: "currentCTCStatus", disabled: true })}
          {renderInput({ label: "Annual CTC", name: "annualCTC", disabled: true })}
          {renderInput({ label: "Monthly CTC", name: "monthlyCTC", disabled: true })}
          {renderInput({ label: "Monthly Gross", name: "monthlyGross", disabled: true })}
          {renderInput({ label: "Annual Employer Cost", name: "annualEmployerCost", disabled: true })}
          {renderInput({ label: "Monthly Net Pay Estimate", name: "netPayEstimateMonthly", disabled: true })}
          {renderInput({ label: "CTC Last Approved On", name: "cTCLastApprovedOn", type: "date", disabled: true })}
          {renderInput({ label: "CTC Revision Number", name: "cTCRevisionNumber", disabled: true })}
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>Profile & Onboarding Tracking</h2>
        <div style={gridStyle}>
          {renderSelect({ label: "Profile Completion Status", name: "profileCompletionStatus", options: ["Not Started", "In Progress", "Submitted", "Completed"] })}
          {renderSelect({ label: "Profile Verification Status", name: "profileVerificationStatus", options: ["Not Verified", "Pending HR Review", "Verified", "Correction Required"] })}
          {renderSelect({ label: "Onboarding Stage", name: "onboardingStage", options: ["Offered", "Preboarding", "Profile Pending", "HR Verification", "Onboarding", "Active"] })}
        </div>
      </div>

      <div style={actionBarStyle}>
        <button type="button" onClick={handleClear} style={secondaryButtonStyle} disabled={saving}>Clear</button>
        <button type="button" onClick={handleSave} style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }} disabled={saving}>
          {saving ? "Saving..." : "Save Employee"}
        </button>
      </div>

      <EmployeeMasterTable employees={employees} loading={loadingEmployees} onRefresh={loadEmployees} onEdit={handleEditEmployee} onViewHistory={handleViewPolicyHistory} />
      <EmployeePolicyHistoryPanel employeeId={policyHistoryEmployeeId} history={policyHistoryRows} loading={loadingPolicyHistory} onClose={() => setPolicyHistoryEmployeeId("")} />
    </div>
  );
}

const pageStyle = { fontFamily: "Segoe UI, Calibri, Arial, sans-serif", padding: "18px", backgroundColor: "var(--fm-content)", minHeight: "100vh", color: "var(--fm-text)", boxSizing: "border-box", overflowX: "hidden" };
const headerStyle = { marginBottom: "16px", background: "var(--fm-hero-bg)", color: "#ffffff", borderRadius: "20px", padding: "22px 24px", boxShadow: "0 10px 28px rgba(0,80,150,0.16)" };
const cardStyle = { backgroundColor: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "18px", padding: "16px", marginBottom: "14px", boxShadow: "0 3px 13px rgba(15,23,42,0.052)", minWidth: 0, overflow: "hidden" };
const sectionTitleStyle = { marginTop: 0, marginBottom: "12px", fontSize: "18px", color: "var(--fm-text)" };
const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px", minWidth: 0 };
const fieldStyle = { display: "flex", flexDirection: "column", minWidth: 0 };
const labelStyle = { marginBottom: "6px", fontWeight: 750, color: "var(--fm-text)", fontSize: "12.5px" };
const inputStyle = { height: "40px", border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "8px 10px", fontSize: "13px", boxSizing: "border-box", width: "100%", maxWidth: "100%", minWidth: 0 };
const errorStyle = { marginTop: "5px", color: "#b91c1c", fontSize: "12px" };
const messageStyle = { border: "1px solid", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 };
const noteStyle = { color: "var(--fm-muted)", fontSize: "13px", marginTop: "-4px", marginBottom: "14px" };
const infoStyle = { border: "1px solid #bfdbfe", backgroundColor: "#eef6ff", color: "var(--fm-page-accent)", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 };
const assignmentNoteStyle = { marginTop: "14px", border: "1px solid #bfdbfe", backgroundColor: "#eef6ff", color: "var(--fm-page-accent)", borderRadius: "13px", padding: "12px", fontSize: "12.5px", fontWeight: 700 };
const editingStyle = { border: "1px solid #fed7aa", backgroundColor: "#fff7ed", color: "#b45309", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 };
const actionBarStyle = { display: "flex", justifyContent: "flex-end", gap: "10px", padding: "14px 0", flexWrap: "wrap" };
const primaryButtonStyle = { backgroundColor: "var(--fm-page-accent)", color: "#ffffff", border: "1px solid #0078d4", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const secondaryButtonStyle = { backgroundColor: "#eef6ff", color: "var(--fm-page-accent)", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const payScaleInfoStyle = { marginTop: "14px", border: "1px solid #bfdbfe", backgroundColor: "#eef6ff", color: "var(--fm-page-accent)", borderRadius: "13px", padding: "12px", fontSize: "12.5px", fontWeight: 700 };
const payScaleWarningStyle = { marginTop: "14px", border: "1px solid #fed7aa", backgroundColor: "#fff7ed", color: "#b45309", borderRadius: "13px", padding: "12px", fontSize: "12.5px", fontWeight: 700 };
const payScaleBandCardStyle = { marginTop: "14px", border: "1px solid #bbf7d0", backgroundColor: "#ecfdf5", color: "#047857", borderRadius: "14px", padding: "14px" };
const payScaleBandHeaderStyle = { display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", marginBottom: "10px", fontSize: "13px" };
const payScaleBandGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", fontSize: "12.5px" };
export default EmployeeMaster;
